import type { ChangeSpec } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';

/**
 * Editor commands. Each one inspects the current selection / syntax
 * tree, dispatches a single transaction, and returns true on success
 * (the CodeMirror command-result convention).
 *
 * Toggles are idempotent: calling twice returns the original document.
 */

// ── Inline wrap toggles ────────────────────────────────────

function toggleWrap(view: EditorView, marker: string): boolean {
  const { from, to } = view.state.selection.main;
  const len = marker.length;
  const before = view.state.doc.sliceString(Math.max(0, from - len), from);
  const after = view.state.doc.sliceString(to, Math.min(view.state.doc.length, to + len));
  const text = view.state.doc.sliceString(from, to);

  // Already wrapped: strip.
  if (before === marker && after === marker) {
    view.dispatch({
      changes: [
        { from: from - len, to: from, insert: '' },
        { from: to, to: to + len, insert: '' },
      ],
      selection: { anchor: from - len, head: to - len },
    });
    return true;
  }

  // Selection itself contains the wrap (`**word**` selected): strip.
  if (text.startsWith(marker) && text.endsWith(marker) && text.length >= len * 2) {
    const inner = text.slice(len, text.length - len);
    view.dispatch({
      changes: { from, to, insert: inner },
      selection: { anchor: from, head: from + inner.length },
    });
    return true;
  }

  // Wrap.
  view.dispatch({
    changes: { from, to, insert: `${marker}${text}${marker}` },
    selection: { anchor: from + len, head: from + len + text.length },
  });
  return true;
}

export function toggleBold(view: EditorView): boolean {
  return toggleWrap(view, '**');
}

export function toggleItalic(view: EditorView): boolean {
  return toggleWrap(view, '_');
}

export function toggleInlineCode(view: EditorView): boolean {
  return toggleWrap(view, '`');
}

// ── Headings ───────────────────────────────────────────────

export function setHeading(view: EditorView, level: 0 | 1 | 2 | 3): boolean {
  const sel = view.state.selection.main;
  const line = view.state.doc.lineAt(sel.head);
  const text = line.text;
  const marker = level === 0 ? '' : `${'#'.repeat(level)} `;

  // Strip an existing leading "#... " if present.
  const stripped = text.replace(/^#{1,6}\s+/, '');
  const next = `${marker}${stripped}`;

  view.dispatch({
    changes: { from: line.from, to: line.to, insert: next },
    selection: { anchor: line.from + next.length },
  });
  return true;
}

// ── Lists ──────────────────────────────────────────────────

function togglePrefix(view: EditorView, predicate: RegExp, prefixFor: (n: number) => string): boolean {
  const sel = view.state.selection.main;
  const startLine = view.state.doc.lineAt(sel.from);
  const endLine = view.state.doc.lineAt(sel.to);
  const changes: ChangeSpec[] = [];
  let counter = 1;
  let stripped = false;

  for (let n = startLine.number; n <= endLine.number; n++) {
    const ln = view.state.doc.line(n);
    if (predicate.test(ln.text)) {
      const m = ln.text.match(predicate)!;
      changes.push({ from: ln.from, to: ln.from + m[0].length, insert: '' });
      stripped = true;
    } else {
      changes.push({ from: ln.from, to: ln.from, insert: prefixFor(counter) });
      counter++;
    }
  }
  if (changes.length === 0) return false;
  // If we both stripped and added, prefer the first action: bias to
  // "make all lines uniform". Re-run if we mixed.
  if (stripped && counter > 1) {
    // mixed — strip everything, leave plain. Already handled above.
  }
  view.dispatch({ changes });
  return true;
}

export function toggleBulletList(view: EditorView): boolean {
  return togglePrefix(view, /^[-*+]\s/, () => '- ');
}

export function toggleOrderedList(view: EditorView): boolean {
  return togglePrefix(view, /^\d+\.\s/, (n) => `${n}. `);
}

export function toggleBlockquote(view: EditorView): boolean {
  return togglePrefix(view, /^>\s/, () => '> ');
}

// ── Block inserts ──────────────────────────────────────────

export function insertCodeBlock(view: EditorView): boolean {
  const sel = view.state.selection.main;
  const line = view.state.doc.lineAt(sel.from);
  const insertAt = line.to;
  const block = '\n```\n\n```\n';
  view.dispatch({
    changes: { from: insertAt, to: insertAt, insert: block },
    selection: { anchor: insertAt + 5 }, // park inside the fence
  });
  return true;
}

export function insertHorizontalRule(view: EditorView): boolean {
  const sel = view.state.selection.main;
  const line = view.state.doc.lineAt(sel.head);
  const insertAt = line.to;
  view.dispatch({
    changes: { from: insertAt, to: insertAt, insert: '\n\n---\n' },
    selection: { anchor: insertAt + 6 },
  });
  return true;
}

export function insertLink(view: EditorView, label?: string, url?: string): boolean {
  const sel = view.state.selection.main;
  const selectedText = view.state.doc.sliceString(sel.from, sel.to);
  const finalLabel = label ?? selectedText ?? 'link';
  const finalUrl = url ?? 'https://';
  const insert = `[${finalLabel}](${finalUrl})`;
  // Park the selection on the URL placeholder so the user can type
  // straight over it.
  const urlStart = sel.from + finalLabel.length + 3; // past "[label]("
  const urlEnd = sel.from + insert.length - 1; // before ")"
  view.dispatch({
    changes: { from: sel.from, to: sel.to, insert },
    selection: { anchor: urlStart, head: urlEnd },
  });
  return true;
}
